import React, { useEffect, useState, useRef } from "react";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { fetchUnitUser, setUnitUser, deleteUnit } from "actions/Units";
import { fetchUserUnits } from "actions/Users";
import { useModal } from "context/ModalContext";
import { returnDateFromTimestamp } from "utilities";
import TabbedSection from "components/TabbedSection";
import UnitDetailModal from "app/routes/Sites/routes/Display/UnitsUsers/UnitDetailModal";
import UnitSettingsModal from "app/routes/Sites/routes/Display/UnitsUsers/UnitSettings/UnitSettingsModal";
import UserDetailModal from "app/routes/Sites/routes/Display/UnitsUsers/UserDetailModal";
import SharedUserDetailModal from "app/routes/Sites/routes/Display/UnitsUsers/SharedUserDetailModal";
import ZonesDetailModal from "app/routes/Sites/routes/Display/UnitsUsers/ZonesDetailModal";
import Tag from "components/Tag";
import LoadingIndicator from "components/LoadingIndicator";
import ButtonIconOnly from "components/ButtonIconOnly";
import Popout from "components/Popout";
import ButtonContained from "components/ButtonContained";

const UnitsUsersDetailModal = ({ unitUser, trigger, updateLocalRentalState, updateLocalShareInfo, updateLocalDeletedUnit }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { openModal, updateModalTitle, updateModalSubTitle } = useModal();
  const data = useSelector(state => state.units.unitUser);
  const site = useSelector(state => state.auth.site);
  const authUser = useSelector(state => state.auth.authUser);
  const userTimezone = useSelector(state => state.app.userTimezone);

  useEffect(() => {
    dispatch(setUnitUser(null));
    const payload = {};
    if (unitUser.id) {
      payload.unitId = unitUser.id;
      payload.unitUUID = unitUser.uuid;
    }
    if (unitUser.User?.id) {
      payload.userId = unitUser.User.id;
      payload.userUUID = unitUser.User.uuid;
      dispatch(fetchUserUnits(unitUser.User.uuid));
    }
    dispatch(fetchUnitUser(
      payload,
      {
        unregisteredUser: intl.formatMessage({ id: "comp.button.unregistered.user" })
      }
    ));
  }, [trigger]);

  const getSubtitle = (item) => {
    if (!item.id || item.accessType === "service" || item.accessType === "employee") {
      return (
        <p className="tw-paragraph-lg text-[var(--color-gray-600)]">
          {item.User?.role?.name}
        </p>
      );
    }

    if (item.rentalState === "auction-smartentry") {
      if (item.auctionType === "unlock") {
        const unlocks = item.auctionUnlock;
        return (
          <Tag
            text={`Auction - ${unlocks} unlock${unlocks > 1 ? "s" : ""} remaining`}
            color="red"
          />
        );
      }

      const expiration = returnDateFromTimestamp(
        item.auctionExpiration,
        authUser,
        site,
        userTimezone
      );

      const userName =
        item.User?.firstName && item.User?.lastName
          ? `- ${item.User.firstName} ${item.User.lastName}`
          : "";

      return (
        <Tag
          text={`Auction - ${expiration} ${userName}`}
          color="red"
        />
      );
    }

    return (
      <Tag
        text={item.rentalStateLabel}
        color={item.rentalStateColor}
      />
    );
  };

  useEffect(() => {
    if (!data) {
      updateModalTitle(null);
      updateModalSubTitle(null);
      return;
    }

    const title = data.unit?.id && data.unit?.accessType !== "service" && data.unit?.accessType !== "employee" ? (
      <div className="flex gap-2 items-center">
        <h4 className="tw-heading-4 text-[var(--color-gray-800)]">{data.unit.name}</h4>
        {(authUser?.permissions?.includes("manage_locks") || authUser?.permissions?.includes("support_rekey")) && <ButtonIconOnly
          id="Unit-Detail-Modal-Settings-Icon"
          dataCy="Unit-Detail-Modal-Settings-Icon"
          icon="settings"
          type="default"
          onClick={() => {
            openModal(
              <UnitSettingsModal 
                uuDetail={data} 
                updateLocalRentalState={updateLocalRentalState}
                unitUserId={unitUser?.id?.toString()}
                updateLocalDeletedUnit={updateLocalDeletedUnit}
              />,
              <UnitSettingsModalHeader
                unitUser={unitUser}
                data={data}
                updateLocalDeletedUnit={updateLocalDeletedUnit}
                updateLocalRentalState={updateLocalRentalState}
                updateLocalShareInfo={updateLocalShareInfo}
              />
            );
          }}
        />}
      </div>
    ) : data.unit.User?.fullName;

    // const title = data.unit?.id && data.unit?.accessType !== "service" ? data.unit.name : data.unit.User?.fullName;
    const subTitle = getSubtitle(data.unit);
    updateModalTitle(title);
    updateModalSubTitle(subTitle);
  }, [data]);

  const getTabs = () => {
    const tabs = [];
    if (data?.unit?.id && data?.unit?.accessType !== "service" && data.unit?.accessType !== "employee") tabs.push({ tab: "Unit", label: intl.formatMessage({ id: "pages.units.Type" }), showLabel: true });
    if (data?.unit?.User?.id) tabs.push({ tab: "User", label: intl.formatMessage({ id: "pages.users.Type" }), showLabel: true });
    if (data?.sharedUsers?.length) tabs.push({ tab: "Shared Users", label: intl.formatMessage({ id: "pages.users.shared" }), showLabel: true });
    if (data?.unit?.accessType === "service") tabs.push({ tab: "Zones", label: intl.formatMessage({ id: "pages.zones.Types" }), showLabel: true });
    return tabs;
  };

  const tabs = getTabs();

  const tabComponents = {
    Unit: <UnitDetailModal uuDetail={data} unitUser={unitUser} updateLocalShareInfo={updateLocalShareInfo} />,
    User: <UserDetailModal uuDetail={data} unitUser={unitUser} />,
    "Shared Users": <SharedUserDetailModal uuDetail={data} />,
    Zones: <ZonesDetailModal uuDetail={data} />
  };

  return (
    <div>
      {
        !data ?
          <div className="flex justify-center">
            <LoadingIndicator />
          </div> :
          data?.unit?.rentalState === "available" ?
            <UnitDetailModal uuDetail={data} unitUser={unitUser} /> :
            !data?.unit.id ?
              <UserDetailModal uuDetail={data} unitUser={unitUser} /> :
              <TabbedSection topPadding={false} tabs={tabs} defaultTab={data?.unit?.id && data?.unit?.accessType !== "service" && data.unit?.accessType !== "employee" ? "Unit" : "User"}>
                {tabComponents}
              </TabbedSection>
      }
    </div>
  )
}

// Separate header component with its own state management
const UnitSettingsModalHeader = ({ unitUser, data, updateLocalDeletedUnit, updateLocalRentalState, updateLocalShareInfo }) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { openModal, closeModal } = useModal();
  const popoutAnchorRef = useRef(null);

  const authUser = useSelector(state => state.auth.authUser);
  const [isPopoutOpen, setIsPopoutOpen] = useState(false);

  return (
    <div className="flex justify-between items-center mr-2">
      <Popout
        isOpen={isPopoutOpen}
        onClose={() => setIsPopoutOpen(false)}
        anchorRef={popoutAnchorRef}
        positioning="bottom-left"
        header={false}>
        <div className="max-w-[300px] gap-3 flex flex-col pt-6">
          <p className="tw-paragraph-lg">{intl.formatMessage({ id: "comp.form.delete.description" })}</p>
          <div className="flex gap-2 justify-start">
            <ButtonContained
              id="Unit-Settings-Modal-Confirm-Delete-Button"
              dataCy="Unit-Settings-Modal-Confirm-Delete-Button"
              text={intl.formatMessage({ id: "comp.button.delete" })}
              type="default"
              onClick={() => {
                dispatch(
                  deleteUnit(
                    unitUser.uuid,
                    {
                      successMessage: intl.formatMessage({ id: "notifications.success.delete" }),
                      failMessage: intl.formatMessage({ id: "notifications.error.fail.noDelete" }),
                      unitUUID: unitUser.uuid
                    }
                  )
                );
                // Update local state to remove the unit from the table
                updateLocalDeletedUnit && updateLocalDeletedUnit(unitUser.id);
                setIsPopoutOpen(false);
                closeModal();
              }}
            />
            <ButtonContained
              id="Unit-Settings-Modal-Cancel-Delete-Button"
              dataCy="Unit-Settings-Modal-Cancel-Delete-Button"
              text={intl.formatMessage({ id: "comp.button.cancel" })}
              type="defaultOutline"
              onClick={() => setIsPopoutOpen(false)}
            />
          </div>
        </div>
      </Popout>

      <div className="flex gap-2 items-center">
        <ButtonIconOnly
          id="Unit-Settings-Modal-Back-Button"
          dataCy="Unit-Settings-Modal-Back-Button"
          icon="arrowLeft"
          type="default"
          onClick={() => openModal(
            <UnitsUsersDetailModal
              unitUser={unitUser}
              updateLocalRentalState={updateLocalRentalState}
              updateLocalShareInfo={updateLocalShareInfo}
              updateLocalDeletedUnit={updateLocalDeletedUnit}
            />
          )}
        />
        <h4 className="tw-heading-4 text-[var(--color-gray-800)]">{`${data.unit.name} - ${intl.formatMessage({ id: "pages.settings.Types" })}`}</h4>
      </div>
      {data?.unit?.rentalState === "available" && authUser?.permissions?.includes("delete_units") && <ButtonIconOnly
        ref={popoutAnchorRef}
        id="Lock-Settings-Modal-Delete-Lock-Button"
        dataCy="Lock-Settings-Modal-Delete-Lock-Button"
        icon="trash"
        type="alt"
        size={20}
        customColor="text-[var(--color-alert)]"
        onClick={() => setIsPopoutOpen(true)}
      />}
    </div>
  );
};

export default UnitsUsersDetailModal;